import type { User, Profile, App, Connection } from '../types';

export const mockUser: User = {
  id: 'user-1',
  email: 'demo@example.com',
  name: 'Demo User',
  createdAt: '2024-01-01T00:00:00Z',
  lastLoginAt: '2024-01-15T10:00:00Z',
};

export const mockProfiles: Profile[] = [
  {
    id: 'profile-1',
    userId: 'user-1',
    name: 'Personal',
    description: 'My personal integrations',
    isDefault: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'profile-2',
    userId: 'user-1',
    name: 'Work',
    description: 'Work-related connections',
    isDefault: false,
    createdAt: '2024-01-02T00:00:00Z',
  },
];

export const mockApps: App[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Google\'s email service with powerful integration capabilities',
    category: 'communication',
    iconUrl: 'https://developers.google.com/gmail/images/gmail-icon.png',
    authType: 'oauth2',
    pipedreamAppId: 'gmail',
    tags: ['email', 'google', 'productivity'],
    isPopular: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team communication and collaboration platform',
    category: 'communication',
    iconUrl: 'https://cdn.brandfolder.io/5H442O3W/as/pl546j-7le8zk-afym5u/Slack_Mark_Web.png',
    authType: 'oauth2',
    pipedreamAppId: 'slack',
    tags: ['messaging', 'team', 'collaboration'],
    isPopular: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'The world\'s leading software development platform',
    category: 'development',
    iconUrl: 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
    authType: 'oauth2',
    pipedreamAppId: 'github',
    tags: ['git', 'development', 'code'],
    isPopular: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Create, edit, and share spreadsheets online',
    category: 'productivity',
    iconUrl: 'https://ssl.gstatic.com/docs/spreadsheets/favicon3.ico',
    authType: 'oauth2',
    pipedreamAppId: 'google_sheets',
    tags: ['spreadsheet', 'google', 'data'],
    isPopular: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'All-in-one workspace for notes, tasks, and collaboration',
    category: 'productivity',
    iconUrl: 'https://www.notion.so/images/favicon.ico',
    authType: 'oauth2',
    pipedreamAppId: 'notion',
    tags: ['notes', 'database', 'productivity'],
    isPopular: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Customer relationship management platform',
    category: 'crm',
    iconUrl: 'https://c1.sfdcstatic.com/content/dam/web/en_us/www/images/nav/salesforce-cloud-logo-sm.png',
    authType: 'oauth2',
    pipedreamAppId: 'salesforce',
    tags: ['crm', 'sales', 'customer'],
    isPopular: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
];

export const mockConnections: Connection[] = [
  {
    id: 'conn-1',
    userId: 'user-1',
    profileId: 'profile-1',
    appId: 'gmail',
    appSlug: 'gmail-personal',
    accountName: 'personal@gmail.com',
    pipedreamAccountId: 'pd-acc-1',
    status: 'active',
    createdAt: '2024-01-10T00:00:00Z',
    lastUsed: '2024-01-15T09:30:00Z',
  },
  {
    id: 'conn-2',
    userId: 'user-1',
    profileId: 'profile-2',
    appId: 'slack',
    appSlug: 'slack-work',
    accountName: 'Work Workspace',
    pipedreamAccountId: 'pd-acc-2',
    status: 'active',
    createdAt: '2024-01-12T00:00:00Z',
    lastUsed: '2024-01-15T08:15:00Z',
  },
  {
    id: 'conn-3',
    userId: 'user-1',
    profileId: 'profile-2',
    appId: 'github',
    appSlug: 'github-work',
    accountName: 'work-account',
    pipedreamAccountId: 'pd-acc-3',
    status: 'active',
    createdAt: '2024-01-13T00:00:00Z',
    lastUsed: '2024-01-14T16:45:00Z',
  },
];

// Mock API functions
export const mockApi = {
  async getUser(): Promise<User> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockUser;
  },

  async getProfiles(): Promise<Profile[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockProfiles;
  },

  async getApps(): Promise<App[]> {
    await new Promise(resolve => setTimeout(resolve, 800));
    return mockApps;
  },

  async getConnections(): Promise<Connection[]> {
    await new Promise(resolve => setTimeout(resolve, 400));
    return mockConnections;
  },

  async searchApps(query: string): Promise<App[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    if (!query) return mockApps;
    
    const lowerQuery = query.toLowerCase();
    return mockApps.filter(app => 
      app.name.toLowerCase().includes(lowerQuery) ||
      app.description?.toLowerCase().includes(lowerQuery) ||
      app.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  },
};